import { HttpErrorResponse } from '@angular/common/http';
import { ParsedError } from '@app/shared/models/parsed-error';
import { Translatable } from '@app/translate/models/translatable';
import { RawErrorDetails } from '@app/utils/error/raw-error-details';
import { throwError } from 'rxjs';

/**
 * Error interface that is used to describe application errors.
 */
export interface PortalError {
  /**
   * Underlying error.
   */
  cause: PortalError | ParsedError | HttpErrorResponse | Error | PortalErrors | null;

  /**
   * i18n key that is shown as additional information if the user expands the error
   */
  description?: string;

  /**
   * Indicates whether the description is in HTML format or not.
   */
  descriptionIsHtml?: boolean;

  /**
   * ID that can be used to identify the source of the error.
   */
  id: string;

  /**
   * optional i18n key or translation that directly provides textual representation for this error.
   */
  i18n?: Translatable;
}

/**
 * Error that contains multiple PortalError instances.
 */
interface PortalErrors extends Error {
  errors: PortalError[];
}

/**
 * Type guard that checks if the given value is a PortalError.
 */
export function isPortalError(value: any): value is PortalError {
  return value != null && 'cause' in value && 'id' in value;
}

/**
 * Create a PortalError object.
 */
export function portalError(error: PortalError): PortalError {
  return error;
}

/**
 * Create an error observable that contains the given PortalError.
 */
export function throwPortalError(portalError: PortalError) {
  return throwError(portalError);
}

interface HttpErrorDetails {
  exception?: string;
  resultCode?: string;
}

export function containsHttpError(maybeError: any, test?: HttpErrorDetails): boolean {
  return (
    collectErrors(maybeError, (error) => {
      const isHttpError = isHttpErrorResponse(error);

      if (isHttpError && test != null) {
        const details = error.error as RawErrorDetails;
        const exceptionMatches = test.exception != null && details.exception === test.exception;
        const resultCodeMatches = test.resultCode != null && details.resultCode === test.resultCode;

        return exceptionMatches || resultCodeMatches;
      }

      return isHttpError;
    }).length > 0
  );
}

/**
 * Collects all errors matched by the predicate.
 * Explanation: `PortalError` wraps the actual error cause, that itself might wrap another cause or collection of errors.
 * Hence, we traverse all possible paths and collect every error/cause that is matched by the provided criteria (the predicate function).
 *
 * @param portalError the outer error
 * @param predicate type guard that checks if the error satisfies the criteria to be collected.
 */
export function collectErrors<T extends PortalError | ParsedError | HttpErrorResponse | Error | PortalErrors>(
  portalError: PortalError | ParsedError | HttpErrorResponse | Error | PortalErrors,
  predicate: (error: PortalError | ParsedError | HttpErrorResponse | Error | PortalErrors) => error is T
): T[];

/**
 * Collects all errors matched by the predicate.
 * Explanation: `PortalError` wraps the actual error cause, that itself might wrap another cause or collection of errors.
 * Hence, we traverse all possible paths and collect every error/cause that is matched by the provided criteria (the predicate function).
 *
 * @param portalError the outer error
 * @param predicate function that checks if the error satisfies the criteria to be collected.
 */
export function collectErrors(
  portalError: PortalError | ParsedError | HttpErrorResponse | Error | PortalErrors,
  predicate: (error: PortalError | ParsedError | HttpErrorResponse | Error | PortalErrors) => boolean
): (PortalError | ParsedError | HttpErrorResponse | Error)[];

/**
 * Collects all errors matched by the predicate.
 * Explanation: `PortalError` wraps the actual error cause, that itself might wrap another cause or collection of errors.
 * Hence, we traverse all possible paths and collect every error/cause that is matched by the provided criteria (the predicate function).
 *
 * @param portalError the outer error
 * @param predicate function that checks if the error satisfies the criteria to be collected.
 */
export function collectErrors<T = PortalError | ParsedError | HttpErrorResponse | Error | PortalErrors>(
  portalError: PortalError | ParsedError | HttpErrorResponse | Error | PortalErrors,
  predicate: (err: PortalError | ParsedError | HttpErrorResponse | Error | PortalErrors) => boolean
): (PortalError | ParsedError | HttpErrorResponse | Error)[] {
  const arr: (PortalError | ParsedError | HttpErrorResponse | Error)[] = [];
  if (predicate(portalError)) {
    arr.push(portalError);
  }
  const cause = portalError['cause'];
  if (cause != null) {
    const errs = collectErrors(cause, predicate);
    if (errs.length > 0) {
      arr.push(...errs);
    }
  }
  const portalErrors = portalError['errors'] as PortalErrors;
  if (portalErrors != null && Array.isArray(portalErrors)) {
    for (let e of portalErrors) {
      const errs = collectErrors(e, predicate);
      if (errs.length > 0) {
        arr.push(...errs);
      }
    }
  }

  return arr;
}

/**
 * Type guard that can be used as predicate when calling collectErrors. This will
 automatically narrow the returned type to HttpErrorResponse.
 *
 * @param error Value to check if this is an instance of HttpErrorResponse.
 */
export function isHttpErrorResponse(error: any): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse;
}
